import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useVendorAuth } from "../VendorAuthContext";
import { RevenueData, RevenueTransaction, vendorGetRevenue } from "../vendorApi";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR DASHBOARD — Revenue stats + Chart.js line chart
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  goldLight: "rgba(212,163,115,0.12)",
  white: "#ffffff",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
  bg: "#f1f5f9",
  green: "#10b981",
  greenBg: "#ecfdf5",
  blue: "#3b82f6",
  blueBg: "#eff6ff",
  purple: "#8b5cf6",
  purpleBg: "#f5f3ff",
};

const formatCurrency = (v: number) =>
  v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(2)}`;

const formatCurrencyFull = (v: number) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CHIP: Record<string, { bg: string; color: string }> = {
  paid:      { bg: "#dcfce7", color: "#166534" },
  pending:   { bg: "#fef3c7", color: "#92400e" },
  cancelled: { bg: "#fef2f2", color: "#991b1b" },
};

const VendorDashboard: React.FC = () => {
  const { vendor } = useVendorAuth();
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendorGetRevenue()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  /* Chart config */
  const chartConfig = data
    ? {
        data: {
          labels: data.chart.labels,
          datasets: [
            {
              label: "Revenue",
              data: data.chart.values,
              fill: true,
              borderColor: T.gold,
              backgroundColor: "rgba(212,163,115,0.08)",
              borderWidth: 2.5,
              pointBackgroundColor: T.gold,
              pointBorderColor: T.white,
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: "index" as const },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: T.navy,
              titleFont: { size: 12, weight: 600 as const },
              bodyFont: { size: 13 },
              padding: 12,
              cornerRadius: 8,
              displayColors: false,
              callbacks: {
                label: (ctx: any) => formatCurrencyFull(ctx.raw as number),
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 11 }, color: T.slateLt },
              border: { display: false },
            },
            y: {
              grid: { color: "rgba(0,0,0,0.04)" },
              ticks: {
                font: { size: 11 },
                color: T.slateLt,
                callback: (v: any) => formatCurrency(v as number),
              },
              border: { display: false },
              beginAtZero: true,
            },
          },
        },
      }
    : null;

  const stats = data
    ? [
        {
          label: "Total Revenue",
          value: formatCurrencyFull(data.total_revenue),
          sub: `${data.recent_transactions.length} transactions`,
          icon: <AccountBalanceWalletOutlinedIcon />,
          color: T.green,
          bg: T.greenBg,
        },
        {
          label: "This Month",
          value: formatCurrencyFull(data.monthly_revenue),
          sub: new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
          icon: <AttachMoneyOutlinedIcon />,
          color: T.blue,
          bg: T.blueBg,
        },
        {
          label: "Upcoming Revenue",
          value: formatCurrencyFull(data.upcoming_revenue),
          sub: `${data.upcoming_events} upcoming event${data.upcoming_events !== 1 ? "s" : ""}`,
          icon: <EventOutlinedIcon />,
          color: T.purple,
          bg: T.purpleBg,
        },
        {
          label: "Avg Booking Value",
          value: formatCurrencyFull(data.avg_booking_value),
          sub: "Per transaction",
          icon: <TrendingUpOutlinedIcon />,
          color: T.gold,
          bg: T.goldLight,
        },
      ]
    : [];

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 16 }}>
        <CircularProgress size={32} sx={{ color: T.gold }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3.5 }}>
        <Typography
          sx={{ fontSize: 26, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em" }}
        >
          Welcome back, {vendor?.name?.split(" ")[0] || "Vendor"}
        </Typography>
        <Typography sx={{ fontSize: 14, color: T.slate, mt: 0.3 }}>
          {vendor?.company_name ? `${vendor.company_name} — ` : ""}Revenue overview and recent activity
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2.5,
          mb: 3,
        }}
      >
        {stats.map((s) => (
          <Box
            key={s.label}
            sx={{
              bgcolor: T.white,
              borderRadius: "14px",
              p: 2.5,
              border: `1px solid ${T.line}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              transition: "all 0.2s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slate }}>{s.label}</Typography>
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: "10px", bgcolor: s.bg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  "& .MuiSvgIcon-root": { fontSize: 18, color: s.color },
                }}
              >
                {s.icon}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.navy, lineHeight: 1 }}>
              {s.value}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: T.slateLt, mt: 0.6 }}>{s.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* Chart + Recent Transactions */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "5fr 3fr" }, gap: 2.5 }}>
        {/* Revenue Chart */}
        <Box
          sx={{
            bgcolor: T.white, borderRadius: "14px", border: `1px solid ${T.line}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)", p: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy }}>Revenue Over Time</Typography>
              <Typography sx={{ fontSize: 11.5, color: T.slateLt, mt: 0.2 }}>Last 12 months</Typography>
            </Box>
            {data && (
              <Box
                sx={{
                  px: 1.5, py: 0.6, borderRadius: "8px",
                  bgcolor: data.monthly_revenue > 0 ? T.greenBg : "#f8fafc",
                  border: `1px solid ${data.monthly_revenue > 0 ? "#bbf7d0" : T.line}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11, fontWeight: 600,
                    color: data.monthly_revenue > 0 ? "#166534" : T.slateLt,
                  }}
                >
                  {data.monthly_revenue > 0 ? `+${formatCurrencyFull(data.monthly_revenue)} this month` : "No revenue this month"}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ height: 280, position: "relative" }}>
            {chartConfig ? (
              <Line data={chartConfig.data} options={chartConfig.options} />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Typography sx={{ fontSize: 13, color: T.slateLt }}>No chart data available</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Recent Transactions */}
        <Box
          sx={{
            bgcolor: T.white, borderRadius: "14px", border: `1px solid ${T.line}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)", p: 3,
            display: "flex", flexDirection: "column",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy, mb: 2 }}>
            Recent Transactions
          </Typography>
          {!data || data.recent_transactions.length === 0 ? (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4 }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 36, color: T.slateLt, opacity: 0.4, mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: T.slateLt }}>No transactions yet</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.3 }}>Revenue will appear when payments are processed</Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {data.recent_transactions.map((txn, i) => (
                <Box
                  key={txn.id}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    py: 1.5, borderTop: i > 0 ? `1px solid ${T.line}` : "none",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: T.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {txn.event_name || txn.description || txn.booking_ref || "Transaction"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.3 }}>
                      {txn.booking_ref && (
                        <Typography sx={{ fontSize: 10.5, color: T.slateLt }}>{txn.booking_ref}</Typography>
                      )}
                      <Typography sx={{ fontSize: 10.5, color: T.slateLt }}>
                        {new Date(txn.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right", ml: 1.5, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>
                      {formatCurrencyFull(txn.amount)}
                    </Typography>
                    <Chip
                      label={txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                      size="small"
                      sx={{
                        height: 18, fontSize: 9, fontWeight: 600, mt: 0.3,
                        bgcolor: STATUS_CHIP[txn.status]?.bg || "#f1f5f9",
                        color: STATUS_CHIP[txn.status]?.color || T.slate,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VendorDashboard;
